import { computed, reactive, readonly, shallowRef, toRef, watch } from 'vue';

import dayjs from 'dayjs';

import { useTransactions } from '@/composables/useTransactions';
import type {
  TransactionGroup,
  TransactionWithGroupProperties
} from '@/composables/useTransactionsView.types';
import { useTransactionsViewFilterer } from '@/composables/useTransactionsViewFilterer';
import { useTransactionsViewGroupper } from '@/composables/useTransactionsViewGroupper';
import { formatTimestampRelativeOrUTC } from '@/helpers/formatters';
import { getTitle } from '@/helpers/transactions';
import { TransactionStatusOTC } from '@/references/axios/transactions/types';
import { i18n } from '@/references/i18n';

const TRANSACTIONS_PAGE_LIMIT = 10;

const state = reactive<{
  searchValue: string;
  limit: number;
  loaded: boolean;
}>({
  searchValue: '',
  limit: TRANSACTIONS_PAGE_LIMIT,
  loaded: false
});

const unfilteredGroups = shallowRef<TransactionGroup[]>([]);
const filteredGroups = shallowRef<TransactionGroup[]>([]);
const { transactions, loaded } = useTransactions();
const { groupperStatus, groupperTerminate, groupTransactions } = useTransactionsViewGroupper();
const { filtererStatus, filtererTerminate, filterGroups } = useTransactionsViewFilterer();

watch(
  [transactions, loaded],
  async () => {
    if (!loaded.value) {
      return;
    }

    const txsWithSearchTokens = transactions.value.map((tx): TransactionWithGroupProperties => {
      const isProcessing =
        tx.status === TransactionStatusOTC.Processing || tx.status === TransactionStatusOTC.Pending;
      const startOfDay = dayjs.unix(tx.createdAt).startOf('day').unix();
      return {
        ...tx,
        title: getTitle(tx).toLowerCase(),
        groupId: isProcessing ? -1 : startOfDay,
        groupTitle: isProcessing
          ? i18n.global.t('activity.listSections.pending')
          : formatTimestampRelativeOrUTC(startOfDay)
      };
    });

    if (groupperStatus.value === 'RUNNING') {
      groupperTerminate();
    }

    try {
      unfilteredGroups.value = await groupTransactions(txsWithSearchTokens);
    } catch {
      // suppress error
    }

    state.loaded = true;
  },
  { immediate: true }
);

watch(
  () => ({ ug: unfilteredGroups.value, sv: state.searchValue }),
  async ({ ug, sv }) => {
    if (filtererStatus.value === 'RUNNING') {
      filtererTerminate();
    }

    try {
      filteredGroups.value = await filterGroups(ug, sv);
    } catch {
      // suppress error
    }
  }
);

const total = computed((): number => unfilteredGroups.value.length);

const filteredTotal = computed((): number => filteredGroups.value.length);

const viewedGroups = computed((): TransactionGroup[] => {
  return filteredGroups.value.slice(0, state.limit);
});

const hasMore = computed((): boolean => state.limit < filteredTotal.value);

function showMore(): void {
  if (hasMore.value) {
    state.limit += TRANSACTIONS_PAGE_LIMIT;
  }
}

function resetLimit(): void {
  if (state.limit !== TRANSACTIONS_PAGE_LIMIT) {
    state.limit = TRANSACTIONS_PAGE_LIMIT;
  }
}

function updateSearchValue(value: string): void {
  window.scrollTo(0, 0);

  state.searchValue = value;
}

function resetSearch(): void {
  state.searchValue = '';
}

function reset(): void {
  resetLimit();
  resetSearch();
}

watch(
  () => state.searchValue,
  () => {
    resetLimit();
  }
);

export const useTransactionsView = () => {
  return {
    searchValue: readonly(toRef(state, 'searchValue')),
    loaded: readonly(toRef(state, 'loaded')),
    groups: viewedGroups,
    total,
    hasMore,
    showMore,
    resetLimit,
    updateSearchValue,
    resetSearch,
    reset
  };
};
